import { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUserShield,
  faHeadset,
  faCircle,
  faComment,
} from '@fortawesome/free-solid-svg-icons';
import { Icon } from '@iconify/react';
import ChatWindow from './ChatWindow';
import {
  useUnreadMessageCount,
  useChatMessages,
} from '../../hooks/useChatMessages';
import { motion, AnimatePresence } from 'framer-motion';
import { useUserProfile } from '../../hooks/useUserProfile';
import {
  useUserProfilesByIds,
  normalizeAvatarUrl,
} from '../../hooks/useUserProfiles';
import Avatar from '../ui/Avatar';

/**
 * Chat Manager for Regular Users - WhatsApp style design
 * Shows support chat with ability to distinguish between admin and agent messages
 * Shows profiles of who is responding (like WhatsApp)
 * Mobile: Fullscreen
 * Desktop: Compact widget (600px × 650px) - same as admin
 */
export default function UserChatManager() {
  const location = useLocation();
  const isHome = location.pathname === '/';
  const [isOpen, setIsOpen] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const { data: userProfile } = useUserProfile(currentUser?.id);
  const { data: unreadCount = 0 } = useUnreadMessageCount(conversationId);

  // Get messages to determine who is responding (admin or agent)
  const { messages = [] } = useChatMessages({
    conversationId: conversationId || '',
    enabled: !!conversationId,
  });

  // Get unique sender IDs from messages (admin/agent who responded + current user)
  const responderIds = useMemo(() => {
    const ids = new Set(
      messages
        .filter(
          (msg) => msg.sender_role === 'admin' || msg.sender_role === 'agent',
        )
        .map((msg) => msg.sender_id),
    );
    // Add current user ID to fetch their profile too
    if (currentUser?.id) {
      ids.add(currentUser.id);
    }
    return Array.from(ids);
  }, [messages, currentUser?.id]);

  // Fetch profiles of admins/agents who responded + current user
  const { data: responderProfilesMap = new Map() } =
    useUserProfilesByIds(responderIds);

  // Get the last admin/agent who responded with their profile
  const lastResponder = useMemo(() => {
    const lastMsg = messages
      .filter(
        (msg) => msg.sender_role === 'admin' || msg.sender_role === 'agent',
      )
      .sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      )[0];

    if (!lastMsg) return null;

    const profile = responderProfilesMap.get(lastMsg.sender_id);
    return {
      ...lastMsg,
      profile,
    };
  }, [messages, responderProfilesMap]);

  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();

        if (!user) {
          setConversationId(null);
          return;
        }

        setCurrentUser(user);

        // Use user_id as conversation_id for support conversations
        // Role check will be done using useUserProfile hook (React Query cache)
        setConversationId(user.id);
      } catch (error) {
        console.error('Error fetching user info for chat:', error);
        setConversationId(null);
      }
    };

    fetchUserInfo();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(() => {
      fetchUserInfo();
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // Prevent body scroll on mobile when chat is open
  useEffect(() => {
    if (isOpen && window.innerWidth < 768) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';

      return () => {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen]);

  // Show chat button only on Home for regular users.
  if (!isHome) {
    return null;
  }

  // Don't show chat component for admin/agent users (they use HomeChatManager)
  // But DO show for regular users even if loading (prevents flash/close issue)
  if (userProfile?.role === 'admin' || userProfile?.role === 'agent') {
    return null;
  }

  // Determine chat title and header info
  const chatTitle = lastResponder
    ? `${lastResponder.sender_role === 'admin' ? 'Admin' : 'Agent'}: ${lastResponder.sender_name}`
    : 'Support Chat';

  // Get responder info (admin/agent) - prioritize sender_avatar_url from messages
  const responderAvatarUrl =
    lastResponder?.sender_avatar_url ||
    (lastResponder?.profile?.avatar_url
      ? normalizeAvatarUrl(lastResponder.profile.avatar_url)
      : null);
  const responderName =
    lastResponder?.profile?.full_name ||
    lastResponder?.sender_name ||
    'Support';
  const responderEmail =
    lastResponder?.profile?.email || lastResponder?.sender_email || '';

  return (
    <>
      {/* Floating Chat Button - stays below header; when chat is open, popup is on top of everything */}
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        className="
          fixed
          bottom-6 right-4 sm:bottom-6 sm:right-6
          w-14 h-14
          bg-blue-600 hover:bg-blue-700 active:bg-blue-800
          text-white rounded-full
          shadow-xl hover:shadow-2xl
          z-[40]
          flex items-center justify-center
          transition-all duration-300 touch-manipulation
          ring-4 ring-blue-500/20 hover:ring-blue-500/40
        "
        whileHover={{ scale: 1.05, boxShadow: '0 20px 25px -5px rgba(59, 130, 246, 0.4)' }}
        whileTap={{ scale: 0.95 }}
        aria-label="Open support chat"
        animate={{
          scale: isOpen ? 0 : 1,
          opacity: isOpen ? 0 : 1,
        }}
        style={{ pointerEvents: isOpen ? 'none' : 'auto' }}
      >
        <AnimatePresence mode="wait">
          {isOpen ? (
            <motion.div key="close" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }}>
              <Icon icon="mdi:close" className="w-6 h-6" />
            </motion.div>
          ) : (
            <motion.div key="chat" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }}>
              <Icon icon="mdi:comment-text-outline" className="w-6 h-6" />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Unread messages badge */}
        {!isOpen && unreadCount > 0 && (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center border-2 border-white"
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </motion.div>
        )}
      </motion.button>

      {/* Chat Window - WhatsApp Style (same as admin); on top of everything when open */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className="
              fixed
              top-0 left-0 right-0 bottom-0
              sm:top-auto sm:left-auto sm:right-6 sm:bottom-20
              sm:w-[600px] sm:h-[650px] sm:max-h-[calc(100vh-140px)]
              z-[100]
              shadow-2xl
              sm:rounded-lg
              overflow-hidden
              border-0 sm:border border-gray-200
              bg-white
              flex flex-col
            "
            style={{
              position: 'fixed',
            }}
          >
            {/* Background illustration */}
            <div className="absolute inset-0 opacity-[0.03] pointer-events-none overflow-hidden z-0">
              <img
                src="/core/cyan-blur.png"
                alt=""
                className="w-full h-full object-cover"
                style={{ transform: 'scale(1.2)' }}
              />
            </div>
            <div
              className="relative z-10 flex flex-col h-full"
              style={{
                touchAction: 'none',
              }}
              onTouchMove={(e) => {
                e.stopPropagation();
              }}
            >
              {/* Header with responder profile (like WhatsApp) */}
              <div className="flex items-center justify-between p-3 border-b border-gray-200 bg-white flex-shrink-0">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  {lastResponder ? (
                    <>
                      <Avatar
                        src={responderAvatarUrl}
                        name={responderName}
                        email={responderEmail}
                        size="md"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-gray-900 text-base truncate">
                            {responderName}
                          </h3>
                          {lastResponder.sender_role === 'admin' && (
                            <span className="bg-purple-100 text-purple-700 text-[10px] px-1.5 py-0.5 rounded-full flex items-center gap-1 flex-shrink-0">
                              <FontAwesomeIcon icon={faUserShield} className="w-2.5 h-2.5" />
                              <span>Admin</span>
                            </span>
                          )}
                          {lastResponder.sender_role === 'agent' && (
                            <span className="bg-blue-100 text-blue-700 text-[10px] px-1.5 py-0.5 rounded-full flex items-center gap-1 flex-shrink-0">
                              <FontAwesomeIcon icon={faHeadset} className="w-2.5 h-2.5" />
                              <span>Agent</span>
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 truncate">
                          {responderEmail}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-500 flex-shrink-0">
                        <FontAwesomeIcon icon={faCircle} className="w-2 text-green-500" />
                        <span>Online</span>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <FontAwesomeIcon icon={faComment} className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-900 text-base truncate">
                          Support Chat
                        </h3>
                        <p className="text-xs text-gray-500">
                          We'll respond soon
                        </p>
                      </div>
                    </>
                  )}
                </div>
                <button
                  onClick={() => setIsOpen(false)}
                  className="flex items-center justify-center w-9 h-9 rounded-full text-[#003380] hover:text-[#002660] hover:bg-gray-100 transition-colors flex-shrink-0"
                  aria-label="Close chat"
                >
                  <Icon icon="mdi:close" className="w-5 h-5" />
                </button>
              </div>

              {/* Chat Window */}
              <div
                className="flex-1 min-h-0 overflow-hidden relative z-10"
                style={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                {!conversationId ? (
                  <div className="flex items-center justify-center h-full bg-white">
                    <div className="flex flex-col items-center gap-3">
                      <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                      <p className="text-gray-500 text-sm">Loading chat...</p>
                    </div>
                  </div>
                ) : (
                  <ChatWindow
                    conversationId={conversationId}
                    conversationTitle={chatTitle}
                    className="h-full border-0 rounded-none"
                  />
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Overlay to close on click outside - Desktop only; below chat window but above header */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsOpen(false)}
            className="
            fixed inset-0
            bg-black bg-opacity-20
            z-[99]
            hidden
            sm:block
          "
          />
        )}
      </AnimatePresence>
    </>
  );
}
